//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import { useState } from "react";
import { Button, Checkbox, Header, Icon, Label, Message, Modal, Popup, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { MasterPackPlan as ApiMasterPackPlan, PackPlan as ApiPackPlan, PackPlanStatus } from "../../api/data/v1/idata";
import { Permission } from "../../util/Permission";
import { compareAvailabilityZone, normalizePackPlanStatus } from "./packPlan";

const PACK_JOB_TYPE = "deployment.pack";
const mono = { fontFamily: "monospace", fontSize: 12 } as const;

type PackPlanPanelProps = {
  deploymentId?: string;
  plan?: ApiMasterPackPlan;
  loading?: boolean;
  error?: string;
  requireComment?: (commentArgs: { defaultComment: string; loadingMessage: string; onCommentConfirmation: () => Promise<void> }) => void;
  requirePermission?: (permission: Permission) => boolean;
  /** Survives tab re-renders when the comment modal updates parent state. */
  onPackJobMessage?: (message: string | undefined) => void;
  /** Fresh plan from the execute dialog, so sibling panels stay on the same plan. */
  onPlan?: (plan: ApiMasterPackPlan) => void;
  /** Called when the user clicks Refresh plan, so the parent reloads the plan and sibling panels. */
  onRefresh?: () => void;
};

const resolvePackKey = (pack: ApiPackPlan): string | undefined => {
  const raw = pack as ApiPackPlan & { packKey?: string };
  const key = raw.pack_key || raw.packKey || pack.dbserver?.server_id;
  const trimmed = key?.trim();
  return trimmed || undefined;
};

const bootCandidates = (pack: ApiPackPlan): string[] => pack.boot_set?.pod_refs || [];

const statusLabel = (status?: PackPlanStatus | string | null) => {
  switch (normalizePackPlanStatus(status)) {
    case PackPlanStatus.PACK_PLAN_STATUS_READY:
      return { color: "blue" as const, text: "not packed" };
    case PackPlanStatus.PACK_PLAN_STATUS_ALREADY_PACKED:
      return { color: "green" as const, text: "packed" };
    case PackPlanStatus.PACK_PLAN_STATUS_INELIGIBLE:
      return { color: "orange" as const, text: "ineligible" };
    default:
      return { color: undefined, text: "unknown" };
  }
};

// An omitted list means datad selected nothing (no platform pods). Do not fall back
// to every candidate; that checks deployment members.
const defaultBootSelection = (pack: ApiPackPlan): string[] => pack.boot_set?.default_selected_pod_refs ?? [];

const podNamespace = (ref: string) => ref.split("/")[0] || "";
const podName = (ref: string) => ref.split("/")[1] || "";

// Member pods are named deployment-prmr/crdn/agnt/sngl/gway/ea-*. Platform pods in a
// deployment namespace do not use those names.
const isDeploymentMemberRef = (ref: string) => {
  const name = podName(ref).toLowerCase();
  return (
    name.includes("-prmr-") || name.includes("-crdn-") || name.includes("-agnt-") || name.includes("-sngl-") || name.includes("-gway-") || name.includes("-ea-")
  );
};

const isThisDeploymentMemberRef = (ref: string, deploymentId?: string) =>
  !!deploymentId && podNamespace(ref).includes(deploymentId) && isDeploymentMemberRef(ref);

type PullPod = { label: string; podName: string; nodeName: string };

// Same rule as datad packRestartPodNames: members whose pod is not already on the target.
const pullPods = (pack: ApiPackPlan): PullPod[] => {
  const target = pack.target_node_name || "";
  if (!target) {
    return [];
  }
  const rows: { label: string; member?: { pod_name?: string; node_name?: string } }[] = [
    { label: "dbserver", member: pack.dbserver },
    { label: "coord", member: pack.coordinator },
    { label: "gway", member: pack.gateway },
    { label: "lb", member: pack.load_balancer },
  ];
  const out: PullPod[] = [];
  rows.forEach((row) => {
    const podName = row.member?.pod_name || "";
    if (!podName || row.member?.node_name === target) {
      return;
    }
    out.push({ label: row.label, podName, nodeName: row.member?.node_name || "—" });
  });
  return out;
};

const packExecuteComment = (pack: ApiPackPlan, packKey: string, bootPodRefs: string[], restartPodNames: string[]) => {
  const lines = [`primary: ${packKey}`, `node: ${pack.target_node_name || "—"}`];
  if (bootPodRefs.length === 0) {
    lines.push("boot: none");
  } else {
    lines.push("boot:");
    bootPodRefs.forEach((ref) => lines.push(`- ${ref}`));
  }
  if (restartPodNames.length === 0) {
    lines.push("pull: none");
  } else {
    lines.push("pull:");
    restartPodNames.forEach((name) => lines.push(`- ${name}`));
  }
  return lines.join("\n");
};

const memberDisplayName = (label: string, m?: { role?: string; server_id?: string; pod_name?: string }) => {
  if (!m?.pod_name && !m?.server_id) {
    return undefined;
  }
  return m.server_id || m.pod_name || label;
};

type MemberPlacement = "on-target" | "off-target" | "missing" | "unknown";

const memberPlacement = (hasMember: boolean, nodeName: string | undefined, targetNode: string | undefined): MemberPlacement => {
  if (!hasMember) {
    return "missing";
  }
  if (!targetNode) {
    return "unknown";
  }
  if (!nodeName) {
    return "off-target";
  }
  return nodeName === targetNode ? "on-target" : "off-target";
};

const memberPlacementMeta = (placement: MemberPlacement) => {
  switch (placement) {
    case "on-target":
      return { icon: "check" as const, color: "#15803d", title: "On pack target node" };
    case "off-target":
      return { icon: "times" as const, color: "#dc2626", title: "Not on pack target node" };
    case "missing":
      return { icon: "minus" as const, color: "#9ca3af", title: "Member not in plan" };
    case "unknown":
      return { icon: "question circle outline" as const, color: "#6b7280", title: "Target node unknown" };
  }
};

const PackMemberRow = ({
  label,
  member,
  targetNode,
}: {
  label: string;
  member?: { role?: string; server_id?: string; pod_name?: string; node_name?: string };
  targetNode?: string;
}) => {
  const name = memberDisplayName(label, member);
  const placement = memberPlacement(!!name, member?.node_name, targetNode);
  const meta = memberPlacementMeta(placement);
  const textColor = meta.color;
  const line = name ? `${name} @ ${member?.node_name || "—"}` : `${label} @ —`;

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 2 }}>
      <Icon name={meta.icon} aria-label={meta.title} title={meta.title} style={{ color: meta.color, margin: 0, lineHeight: "1.5" }} />
      <div style={{ ...mono, color: textColor, flex: 1, wordBreak: "break-all" }}>{line}</div>
    </div>
  );
};

export const PackPlanPanel = ({
  deploymentId,
  plan,
  loading,
  error,
  requireComment,
  requirePermission,
  onPackJobMessage,
  onPlan,
  onRefresh,
}: PackPlanPanelProps) => {
  const [actionError, setActionError] = useState<string | undefined>();
  const [executingKey, setExecutingKey] = useState<string | undefined>();
  const [commentPromptHint, setCommentPromptHint] = useState<string | undefined>();
  const [bootPack, setBootPack] = useState<ApiPackPlan | undefined>();
  const [selectedBoot, setSelectedBoot] = useState<Record<string, boolean>>({});
  const [selectedPull, setSelectedPull] = useState<Record<string, boolean>>({});
  const [openingKey, setOpeningKey] = useState<string | undefined>();

  const canPack = !!requirePermission?.("internal-dashboard.deployment.pack");
  const shownError = actionError || error;

  const onExecute = async (packKey: string | undefined) => {
    setActionError(undefined);
    if (!deploymentId) {
      setActionError("Deployment id missing; reload the page.");
      return;
    }
    if (!requireComment) {
      setActionError("Comment prompt is unavailable on this view; reload the deployment page.");
      return;
    }
    if (!packKey) {
      setActionError("Pack key missing from plan row; click Refresh plan and try again.");
      return;
    }
    setOpeningKey(packKey);
    try {
      const p = await apiClients.idashboardClient.GetPackPlan({ deployment_id: deploymentId });
      onPlan?.(p);
      const fresh = (p.packs || []).find((item) => resolvePackKey(item) === packKey);
      if (!fresh) {
        setActionError(`Pack ${packKey} is no longer in the plan.`);
        return;
      }
      if (normalizePackPlanStatus(fresh.status) !== PackPlanStatus.PACK_PLAN_STATUS_READY) {
        setActionError(executeDisabledReason(fresh) || "This pack cannot be run.");
        return;
      }
      const selected: Record<string, boolean> = {};
      const defaults = defaultBootSelection(fresh);
      bootCandidates(fresh).forEach((ref) => {
        selected[ref] = defaults.indexOf(ref) >= 0;
      });
      const pullSelected: Record<string, boolean> = {};
      pullPods(fresh).forEach((pod) => {
        pullSelected[pod.podName] = true;
      });
      setSelectedBoot(selected);
      setSelectedPull(pullSelected);
      setBootPack(fresh);
    } catch (e) {
      setActionError((e as { message?: string })?.message || String(e));
    } finally {
      setOpeningKey(undefined);
    }
  };

  const confirmBootAndExecute = () => {
    const pack = bootPack;
    const packKey = pack ? resolvePackKey(pack) : undefined;
    if (!pack || !packKey || !deploymentId || !requireComment) {
      setBootPack(undefined);
      return;
    }
    const bootPodRefs = bootCandidates(pack).filter((ref) => selectedBoot[ref]);
    const restartPodNames = pullPods(pack)
      .filter((pod) => selectedPull[pod.podName])
      .map((pod) => pod.podName);
    setBootPack(undefined);
    setCommentPromptHint("Confirm in the dialog: add a comment and click Save Change to start the pack job.");
    requireComment({
      defaultComment: packExecuteComment(pack, packKey, bootPodRefs, restartPodNames),
      loadingMessage: "Starting pack job…",
      onCommentConfirmation: async () => {
        setCommentPromptHint(undefined);
        setExecutingKey(packKey);
        onPackJobMessage?.(undefined);
        try {
          await apiClients.idashboardClient.ExecutePack({
            deployment_id: deploymentId,
            pack_key: packKey,
            boot_pod_refs: bootPodRefs,
            restart_pod_names: restartPodNames,
          });
          onPackJobMessage?.(
            `Started ${PACK_JOB_TYPE} job for pack ${packKey}. Open the Jobs tab on this deployment for status and logs, then refresh the plan when it finishes.`
          );
        } catch (e) {
          onPackJobMessage?.((e as { message?: string })?.message || String(e));
          throw e;
        } finally {
          setExecutingKey(undefined);
        }
      },
    });
  };

  const executeDisabledReason = (pack: ApiPackPlan): string | undefined => {
    const normalizedStatus = normalizePackPlanStatus(pack.status);
    if (normalizedStatus === PackPlanStatus.PACK_PLAN_STATUS_READY) {
      return undefined;
    }
    if (normalizedStatus === PackPlanStatus.PACK_PLAN_STATUS_ALREADY_PACKED) {
      return "The pack job is unavailable because the dbserver and coordinator are already on the target node. Gateway and load balancer may still be elsewhere.";
    }
    if (normalizedStatus === PackPlanStatus.PACK_PLAN_STATUS_INELIGIBLE) {
      return pack.summary || "This pack is ineligible.";
    }
    return "Status is unknown. Refresh the plan and check whether the pack job is available.";
  };

  if (!deploymentId || !canPack) {
    return null;
  }

  const executePodRefs = bootPack ? bootCandidates(bootPack) : [];
  const executePullPods = bootPack ? pullPods(bootPack) : [];
  const allSelected = executePodRefs.length > 0 && executePodRefs.every((ref) => selectedBoot[ref]);
  const someSelected = executePodRefs.some((ref) => selectedBoot[ref]);
  const allPullSelected = executePullPods.length > 0 && executePullPods.every((pod) => selectedPull[pod.podName]);
  const somePullSelected = executePullPods.some((pod) => selectedPull[pod.podName]);
  const nothingSelected = !executePodRefs.some((ref) => selectedBoot[ref]) && !somePullSelected;

  return (
    <div style={{ marginTop: 24 }}>
      <Header as="h4" style={{ marginBottom: 8 }}>
        Pack plan
      </Header>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Deployment-level pack units (1 dbserver + 1 coordinator + gateway/LB when applicable). Run one pack job at a time, then refresh the plan.
      </p>
      <Button
        size="small"
        basic
        loading={loading}
        disabled={loading || !!executingKey}
        onClick={() => {
          setActionError(undefined);
          setCommentPromptHint(undefined);
          onRefresh?.();
        }}
      >
        Refresh plan
      </Button>
      {shownError && <Message negative content={shownError} style={{ marginTop: 12 }} />}
      {commentPromptHint && <Message info content={commentPromptHint} style={{ marginTop: 12 }} />}
      {loading && !plan && <Message info content="Loading pack plan…" style={{ marginTop: 12 }} />}
      {plan?.summary && <Message content={plan.summary} style={{ marginTop: 12 }} />}
      {plan?.packs && plan.packs.length > 0 && (
        <Table compact celled size="small" style={{ marginTop: 12 }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={3}>Pack</Table.HeaderCell>
              <Table.HeaderCell width={2}>Status</Table.HeaderCell>
              <Table.HeaderCell width={8}>Members</Table.HeaderCell>
              <Table.HeaderCell width={1} />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {[...plan.packs]
              .sort((a, b) => {
                const zone = compareAvailabilityZone(a.availability_zone, b.availability_zone);
                if (zone !== 0) {
                  return zone;
                }
                return (resolvePackKey(a) || "").localeCompare(resolvePackKey(b) || "");
              })
              .map((pack) => {
                const packKey = resolvePackKey(pack);
                const normalizedStatus = normalizePackPlanStatus(pack.status);
                const st = statusLabel(pack.status);
                const ready = normalizedStatus === PackPlanStatus.PACK_PLAN_STATUS_READY;
                const executeDisabled = !ready || !!executingKey;
                const disabledReason = executeDisabledReason(pack);
                const executeButton = (
                  <Button
                    type="button"
                    size="mini"
                    primary
                    disabled={executeDisabled || !!openingKey}
                    loading={executingKey === packKey || openingKey === packKey}
                    onClick={() => onExecute(packKey)}
                  >
                    Run pack job
                  </Button>
                );
                return (
                  <Table.Row key={packKey || pack.summary}>
                    <Table.Cell>
                      <div style={{ ...mono, fontWeight: 600, fontSize: 13 }}>{packKey || "—"}</div>
                      <div style={{ ...mono, fontSize: 11, color: "#4b5563", marginTop: 6, wordBreak: "break-all" }}>{pack.target_node_name || "—"}</div>
                      {pack.availability_zone && (
                        <Label size="mini" basic style={{ marginTop: 6 }}>
                          {pack.availability_zone}
                        </Label>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Label size="tiny" color={st.color} basic>
                        {st.text}
                      </Label>
                    </Table.Cell>
                    <Table.Cell style={{ fontSize: 12, lineHeight: 1.5 }}>
                      <PackMemberRow label="dbserver" member={pack.dbserver} targetNode={pack.target_node_name} />
                      <PackMemberRow label="coord" member={pack.coordinator} targetNode={pack.target_node_name} />
                      <PackMemberRow label="gway" member={pack.gateway} targetNode={pack.target_node_name} />
                      <PackMemberRow label="lb" member={pack.load_balancer} targetNode={pack.target_node_name} />
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {executeDisabled && disabledReason ? <Popup content={disabledReason} trigger={<span>{executeButton}</span>} /> : executeButton}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
      )}
      {!loading && plan && (!plan.packs || plan.packs.length === 0) && <Message content="No pack units in the plan." style={{ marginTop: 12 }} />}
      <Modal open={!!bootPack} size="large" onClose={() => setBootPack(undefined)}>
        <Modal.Header>
          Run pack job {bootPack ? resolvePackKey(bootPack) : ""}
          {deploymentId ? ` · ${deploymentId}` : ""}
        </Modal.Header>
        <Modal.Content>
          <div style={{ ...mono, marginBottom: 12 }}>{bootPack?.target_node_name || "—"}</div>
          {bootPack && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Pull</div>
              <p style={{ color: "#6b7280", fontSize: 13 }}>
                Checked pods are pulled onto the target node. They start checked. Uncheck a member to leave it where it is.
              </p>
              {executePullPods.length === 0 ? (
                <div style={{ color: "#6b7280", fontSize: 13 }}>Nothing to pull. Every pack member is already on the target node.</div>
              ) : (
                <Table compact celled size="small">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell collapsing>
                        <Checkbox
                          checked={allPullSelected}
                          indeterminate={somePullSelected && !allPullSelected}
                          onChange={(_, data) => {
                            const checked = !!data.checked;
                            const next: Record<string, boolean> = {};
                            executePullPods.forEach((pod) => {
                              next[pod.podName] = checked;
                            });
                            setSelectedPull(next);
                          }}
                          aria-label="Select all pull pods"
                        />
                      </Table.HeaderCell>
                      <Table.HeaderCell>Pod</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {executePullPods.map((pod) => (
                      <Table.Row key={pod.podName}>
                        <Table.Cell collapsing>
                          <Checkbox
                            checked={!!selectedPull[pod.podName]}
                            onChange={(_, data) => setSelectedPull((prev) => ({ ...prev, [pod.podName]: !!data.checked }))}
                            aria-label={`${pod.label} ${pod.podName}`}
                          />
                        </Table.Cell>
                        <Table.Cell style={{ ...mono, wordBreak: "break-all" }}>
                          {pod.label}: {pod.podName} @ {pod.nodeName}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </div>
          )}
          <p style={{ color: "#6b7280", fontSize: 13 }}>
            Pods checked here are booted off the target node before the pack members are pulled. Leave them all unchecked to share the node. Deployment members
            start unchecked.
          </p>
          {bootPack?.boot_set?.note && <Message info content={bootPack.boot_set.note} />}
          {bootPack && executePodRefs.length === 0 && <Message content="No boot candidates on the target node." />}
          {bootPack && executePodRefs.length > 0 && (
            <Table compact celled size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell collapsing>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={(_, data) => {
                        const checked = !!data.checked;
                        const next: Record<string, boolean> = {};
                        executePodRefs.forEach((ref) => {
                          next[ref] = checked;
                        });
                        setSelectedBoot(next);
                      }}
                      aria-label="Select all boot pods"
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell>Pod</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {executePodRefs.map((ref) => {
                  const thisDeployment = isThisDeploymentMemberRef(ref, deploymentId);
                  return (
                    <Table.Row key={ref} style={thisDeployment ? { background: "#f0f9ff" } : undefined}>
                      <Table.Cell collapsing>
                        <Checkbox
                          checked={!!selectedBoot[ref]}
                          onChange={(_, data) => setSelectedBoot((prev) => ({ ...prev, [ref]: !!data.checked }))}
                          aria-label={ref}
                        />
                      </Table.Cell>
                      <Table.Cell style={{ ...mono, wordBreak: "break-all", fontWeight: thisDeployment ? 600 : 400 }}>
                        <span
                          aria-label={thisDeployment ? "This deployment's member" : "Other pod"}
                          title={thisDeployment ? "This deployment's member" : "Other pod"}
                          style={{ marginRight: 8 }}
                        >
                          {thisDeployment ? "●" : "○"}
                        </span>
                        {ref}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setBootPack(undefined)}>
            Cancel
          </Button>
          <Button primary disabled={nothingSelected} onClick={confirmBootAndExecute}>
            Continue
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};
